import { Component, ChangeDetectionStrategy, inject, signal, computed, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { interval } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  BadgeComponent,
  ButtonDirective,
  CardBodyComponent,
  CardComponent,
  CardHeaderComponent,
  ColComponent,
  ContainerComponent,
  FormControlDirective,
  FormSelectDirective,
  RowComponent,
  SpinnerComponent,
  TableDirective
} from '@coreui/angular-pro';
import { TranslatePipe, TranslateService } from 'i18n';
import { MyCard, MyCardsCardPartner, MyCardsResponse, MyCardsService } from '../../../services/mycards.service';
import { ViacarteBalance, ViacarteTransaction } from '../../../services/viacarte-admin.service';
import { CrossLoginService } from '../../../services/cross-login.service';
import { CardPartnerDataComponent } from '../../../components/card-partner-data/card-partner-data.component';

type ViewState = 'loading' | 'coming_soon' | 'not_eligible' | 'profile_incomplete' | 'start' | 'status' | 'completed';

@Component({
  selector: 'app-mycards-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ContainerComponent,
    RowComponent,
    ColComponent,
    CardComponent,
    CardHeaderComponent,
    CardBodyComponent,
    ButtonDirective,
    BadgeComponent,
    SpinnerComponent,
    FormControlDirective,
    FormSelectDirective,
    TableDirective,
    TranslatePipe,
    CardPartnerDataComponent
  ],
  templateUrl: './mycards-page.component.html',
  styleUrls: ['./mycards-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MyCardsPageComponent {
  private service = inject(MyCardsService);
  private crossLogin = inject(CrossLoginService);
  private router = inject(Router);
  private translate = inject(TranslateService);
  private destroyRef = inject(DestroyRef);

  data = signal<MyCardsResponse | null>(null);
  loading = signal(true);
  error = signal<string | null>(null);
  busy = signal<string | null>(null); // 'start' | 'refresh' | 'buy'
  occupation = signal<string>('');

  // statusNetwork-Codes (bestätigt: 1000 aktiv, 1005 ausgegeben/nicht aktiviert).
  private static readonly CARD_STATUS: Record<number, { key: string; color: string }> = {
    1000: { key: 'viacarte_admin.active',        color: 'success' },
    1005: { key: 'viacarte_admin.not_activated', color: 'warning' },
    1007: { key: 'viacarte_admin.inactive',      color: 'secondary' }
  };

  viewState = computed<ViewState>(() => {
    if (this.loading() && !this.data()) return 'loading';
    const d = this.data();
    if (!d) return 'loading';
    if (!d.viacarte_configured) return 'coming_soon';
    if (!d.eligible) return 'not_eligible';
    if (!d.kyc_started) return d.profile_complete ? 'start' : 'profile_incomplete';
    if (d.viacarte?.completed) return 'completed';
    return 'status';
  });

  // Kartenpartner (cryptocards.cash): Link für jeden User mit Kartenberechtigung,
  // unabhängig vom ViaCarte-Zustand darunter.
  cardPartner = computed<MyCardsCardPartner | null>(() => this.data()?.card_partner ?? null);
  partnerEntitled = computed<boolean>(() => this.cardPartner()?.entitled === true);

  cards = computed<MyCard[]>(() => this.data()?.viacarte?.cards ?? []);
  // Physische Karte ausgegeben, aber noch nicht aktiviert (statusNetwork 1005).
  hasInactivePhysical = computed<boolean>(() =>
    this.cards().some(c => c.card_type_label === 'physical' && c.status_network === 1005));
  accountBalance = computed<ViacarteBalance | null>(() => this.data()?.viacarte?.account_balance ?? null);
  accountCurrency = computed<string>(() => this.accountBalance()?.balanceCurrency || '');
  transactions = computed<ViacarteTransaction[]>(() => this.data()?.viacarte?.transactions ?? []);

  // Client-seitiges Paging der Transaktionsliste.
  readonly txPageSizes = [10, 20, 50, 100];
  txPage = signal(1);
  txPageSize = signal(10);
  txTotalPages = computed(() => Math.max(1, Math.ceil(this.transactions().length / this.txPageSize())));
  pagedTransactions = computed<ViacarteTransaction[]>(() => {
    const size = this.txPageSize();
    const page = Math.min(this.txPage(), this.txTotalPages());
    const start = (page - 1) * size;
    return this.transactions().slice(start, start + size);
  });

  setTxPageSize(size: number): void { this.txPageSize.set(Number(size)); this.txPage.set(1); }
  txGoTo(p: number): void { if (p >= 1 && p <= this.txTotalPages()) this.txPage.set(p); }

  constructor() {
    this.load();

    // Während laufendem KYC alle 20s den Status nachziehen.
    interval(20000).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      const d = this.data();
      if (d?.eligible && d.kyc_started && d.viacarte && !d.viacarte.completed && !d.viacarte.failed) {
        this.refreshStatus(false);
      }
    });
  }

  load(): void {
    this.loading.set(true);
    this.error.set(null);
    this.service.get()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (d) => { this.loading.set(false); this.data.set(d); },
        error: () => { this.loading.set(false); this.error.set(this.t('mycards.load_error')); }
      });
  }

  startKyc(): void {
    const occ = this.occupation().trim();
    if (!occ) { this.error.set(this.t('mycards.occupation_required')); return; }
    this.busy.set('start');
    this.error.set(null);
    this.service.startKyc(occ)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (d) => { this.busy.set(null); this.data.set(d); },
        error: (e) => { this.busy.set(null); this.error.set(e?.error?.error || this.t('mycards.start_error')); }
      });
  }

  refreshStatus(showSpinner = true): void {
    if (showSpinner) this.busy.set('refresh');
    this.service.refresh()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (d) => { this.busy.set(null); this.data.set(d); },
        error: () => { this.busy.set(null); }
      });
  }

  goToProfile(): void {
    this.router.navigateByUrl(`/${this.lang()}/profile`);
  }

  // Karten werden nur auf fac3less verkauft. Auf fac3less selbst reicht die Route;
  // von jedem anderen Board aus geht es per Cross-Login-Handoff hinüber, mit dem
  // Bestellassistenten als Landeseite, damit kein zweiter Login nötig ist.
  buyCard(): void {
    const partner = this.cardPartner();
    if (!partner) return;
    if (partner.purchase_on_site) {
      this.router.navigateByUrl(`/${this.lang()}/${partner.purchase_path}`);
      return;
    }
    this.busy.set('buy');
    this.crossLogin.go(partner.purchase_site, partner.purchase_url ?? undefined, partner.purchase_path);
  }

  private lang(): string {
    return localStorage.getItem('language') || 'en';
  }

  // ViaCarte-Pre-Registration-Status -> Badge-Farbe.
  statusColor(status: string | null | undefined): string {
    switch (status) {
      case 'COMPLETED': return 'success';
      case 'FAILED':
      case 'DECLINED':
      case 'REJECTED': return 'danger';
      case 'PENDING_REVIEW':
      case 'PENDING_ADDITIONAL_DOCS':
      case 'WITH_EXCEPTION_FILES': return 'warning';
      case 'ACCEPTED': return 'primary';
      default: return 'info';
    }
  }

  cardStatusBadge(c: MyCard): string {
    const m = c.status_network != null ? MyCardsPageComponent.CARD_STATUS[c.status_network] : undefined;
    return m?.color ?? 'secondary';
  }

  cardStatusLabel(c: MyCard): string {
    if (c.status_network != null) {
      const m = MyCardsPageComponent.CARD_STATUS[c.status_network];
      return m ? this.t(m.key) : `${this.t('viacarte_admin.status_code')} ${c.status_network}`;
    }
    return this.t('viacarte_admin.unknown_status');
  }

  cardAvailable(c: MyCard): string {
    const b = c.balance;
    if (!b || b.error) return '—';
    return this.formatAmount(b.availableBalanceAmount, b.balanceCurrency);
  }

  formatAmount(amount: number | undefined | null, currency?: string): string {
    if (amount == null) return '—';
    return `${Number(amount).toFixed(2)} ${currency || ''}`.trim();
  }

  formatDate(d: string | null | undefined): string {
    if (!d) return '—';
    return new Date(d).toLocaleString();
  }

  private t(key: string): string {
    return this.translate.translate(key) || key;
  }
}
